/**
 * trng002 (TrainingCardList) list sync.
 *
 * COMPOSITE_ROW: top-level identity = Employee.id.
 * Nested TrainingCard content lives under employee.training_card.
 *
 * Audit scope (Employee, not TrainingCard):
 *   TRNG002_CREATED_AUDIT_SCOPE = employees.created_at + created_by_user
 *   TRNG002_UPDATED_AUDIT_SCOPE = employees.updated_at + last_touched_by_user
 *
 * Canonical table-complete row: GET /api/training_cards/by_employee/{id}
 */

export const TRAINING_CARDS_UPDATED_EVENT = 'training_cards:updated';
export const TRAINING_CARDS_REMOVED_EVENT = 'training_cards:removed';

export const SESSION_SAFE_UPDATE = 'SESSION_SAFE_UPDATE';
export const SESSION_INVALIDATING = 'SESSION_INVALIDATING';

/** Employee audit actors used by trng002 Posledná aktivita / Založenie. */
export const IMPLICIT_UPDATE_AUDIT_FIELDS = Object.freeze([
  'updated_at',
  'last_touched_by_user',
  'last_touched_by_user_id',
]);

export const TRNG002_CREATED_AUDIT_SCOPE = 'employees.created_at+created_by_user';
export const TRNG002_UPDATED_AUDIT_SCOPE = 'employees.updated_at+last_touched_by_user';

export const SAFE_CANONICAL_ROW_ENDPOINT = '/api/training_cards/by_employee/{id}';

const MEMBERSHIP_FIELDS = new Set(['my_status', 'my_state', 'id', 'status']);
const ALWAYS_INVALIDATING_FIELDS = new Set(['training_card', 'card']);

const FIELD_ALIASES = {
  name: ['name'],
  defined_id: ['defined_id'],
  'department.name': ['department.name', 'department_id'],
  department_id: ['department.name', 'department_id'],
  my_state: ['my_state'],
  my_status: ['my_status', 'status'],
  status: ['my_status', 'status'],
  date_of_entry: ['date_of_entry'],
  date_of_departure: ['date_of_departure', 'my_status'],
  description: ['description'],
  id: ['id'],
  training_card: ['training_card', 'my_state'],
  created_at: ['created_at'],
  created_by_user: ['created_at'],
  created_by_user_id: ['created_at'],
  updated_at: ['updated_at'],
  last_touched_by_user: ['updated_at'],
  last_touched_by_user_id: ['updated_at'],
};

function unwrapEmployeeRecord(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && payload.data.id != null) {
    return payload.data;
  }
  return payload;
}

function expandFields(fields) {
  const expanded = new Set();
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (field == null || field === '') return;
    const aliases = FIELD_ALIASES[field] || [field];
    aliases.forEach((alias) => expanded.add(alias));
  });
  return expanded;
}

function hasActiveValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((item) => hasActiveValue(item));
  return String(value).trim() !== '';
}

function isDefaultIdAscSorter(sorter) {
  const field = sorter?.field || sorter?.column;
  const dir = sorter?.dir || 'asc';
  return field === 'id' && dir === 'asc';
}

/**
 * Table-complete Employee composite row for trng002.
 * Requires nested training_card array (may be empty) and audit timestamps.
 */
export function isTableCompleteTrainingCardEmployeeRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  if (row.id == null || row.id === '') return false;
  if (!Array.isArray(row.training_card)) return false;
  return true;
}

export function trainingCardEmployeeRowFromResponse(response) {
  if (response == null || typeof response !== 'object') return null;
  if (response.status === 404 || response.status === 400) return null;
  const data = Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response;
  if (Array.isArray(data)) {
    return data.find((row) => row && typeof row === 'object' && row.id != null) || null;
  }
  if (data && typeof data === 'object' && data.id != null && data.status !== 404) return data;
  return null;
}

export function normalizeTrainingCardListMutationDetail(detail) {
  if (!detail || typeof detail !== 'object') return null;
  if (detail.operation) {
    const row = unwrapEmployeeRecord(detail.row) || (detail.id != null && !detail.row ? null : unwrapEmployeeRecord(detail));
    return {
      operation: detail.operation,
      id: detail.id ?? row?.id ?? null,
      row: row && row.id != null ? row : (isTableCompleteTrainingCardEmployeeRow(unwrapEmployeeRecord(detail)) ? unwrapEmployeeRecord(detail) : row),
      changedFields: Array.isArray(detail.changedFields) ? detail.changedFields : [],
    };
  }
  // Legacy: detail is a raw Employee row.
  const row = unwrapEmployeeRecord(detail);
  if (!row || row.id == null) return null;
  return {
    operation: 'update',
    id: row.id,
    row,
    changedFields: ['training_card', 'my_state', ...IMPLICIT_UPDATE_AUDIT_FIELDS],
  };
}

export function classifyTrainingCardListMutation(event, session = {}) {
  const normalized = normalizeTrainingCardListMutationDetail(event?.detail);
  if (!normalized) return SESSION_INVALIDATING;
  if (normalized.operation !== 'update') return SESSION_INVALIDATING;
  if (!Array.isArray(normalized.changedFields) || normalized.changedFields.length === 0) {
    return SESSION_INVALIDATING;
  }
  const row = normalized.row;
  if (!isTableCompleteTrainingCardEmployeeRow(row)) return SESSION_INVALIDATING;
  const id = normalized.id ?? row.id;
  if (id == null || id === '') return SESSION_INVALIDATING;
  if (typeof session.isRowLoaded !== 'function' || !session.isRowLoaded(id)) return SESSION_INVALIDATING;
  if (String(session.toolbarSearch ?? '').trim() !== '') return SESSION_INVALIDATING;

  const changedFields = Array.from(new Set([
    ...normalized.changedFields,
    ...IMPLICIT_UPDATE_AUDIT_FIELDS,
  ]));
  const expanded = expandFields(changedFields);
  for (const field of expanded) {
    if (MEMBERSHIP_FIELDS.has(field) || ALWAYS_INVALIDATING_FIELDS.has(field)) return SESSION_INVALIDATING;
  }
  if (changedFields.some((field) => ALWAYS_INVALIDATING_FIELDS.has(field) || MEMBERSHIP_FIELDS.has(field))) {
    return SESSION_INVALIDATING;
  }

  const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
  for (const filter of filters) {
    if (!filter?.field || !hasActiveValue(filter.value)) continue;
    const aliases = expandFields([filter.field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
  for (const sorter of sorters) {
    const field = sorter?.field || sorter?.column;
    if (!field) continue;
    if (isDefaultIdAscSorter(sorter) && !expanded.has('id')) continue;
    const aliases = expandFields([field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  return SESSION_SAFE_UPDATE;
}

export function applyPagedMutationOverlay(response, overlay) {
  if (!response || typeof response !== 'object' || !Array.isArray(response.data)) return response;
  if (!overlay || typeof overlay.get !== 'function' || overlay.size === 0) return response;
  return {
    ...response,
    data: response.data.map((row) => {
      if (!row || row.id == null) return row;
      return overlay.get(String(row.id)) || row;
    }),
  };
}

/**
 * Emit a typed list mutation. Prefer this for Package A sync.
 * @param {{ operation: string, id?: *, row?: object, changedFields?: string[] }} detail
 */
export function emitTrainingCardListMutation(detail) {
  window.dispatchEvent(new CustomEvent(TRAINING_CARDS_UPDATED_EVENT, {
    detail: detail && typeof detail === 'object' ? detail : { operation: 'invalidate' },
  }));
}

/**
 * Convenience emitters used by trng003/trng004.
 * Create always invalidates membership. Update carries table-complete Employee row.
 */
export function emitTrainingCardListCreated(row) {
  if (row?.id == null) return;
  emitTrainingCardListMutation({
    operation: 'create',
    id: row.id,
    row,
    changedFields: [],
  });
}

export function emitTrainingCardListUpdated(rowOrDetail, options = {}) {
  if (rowOrDetail?.operation) {
    emitTrainingCardListMutation(rowOrDetail);
    return;
  }
  if (rowOrDetail?.id == null) return;
  const operation = options.operation || 'update';
  if (operation === 'create') {
    emitTrainingCardListCreated(rowOrDetail);
    return;
  }
  const changedFields = Array.isArray(options.changedFields) && options.changedFields.length > 0
    ? options.changedFields
    : ['description', ...IMPLICIT_UPDATE_AUDIT_FIELDS];
  emitTrainingCardListMutation({
    operation: 'update',
    id: rowOrDetail.id,
    row: rowOrDetail,
    changedFields,
  });
}

/**
 * After nested card create/update/activate/deactivate: employee my_state and nested
 * cards can change membership under Stav / show_deactivated → invalidate unless
 * callers pass a proven safe field set.
 */
export function emitTrainingCardListEmployeeSynced(row, options = {}) {
  if (row?.id == null) return;
  const changedFields = Array.isArray(options.changedFields) && options.changedFields.length > 0
    ? options.changedFields
    : ['training_card', 'my_state', ...IMPLICIT_UPDATE_AUDIT_FIELDS];
  emitTrainingCardListMutation({
    operation: options.operation || 'update',
    id: row.id,
    row,
    changedFields,
  });
}

export function emitTrainingCardListRemoved(id) {
  if (id == null || id === '') return;
  window.dispatchEvent(new CustomEvent(TRAINING_CARDS_REMOVED_EVENT, { detail: { id } }));
}
